/**
 * analyticsService.js — HealHub Analytics Tracker
 * ─────────────────────────────────────────────────
 * ROOT CAUSE FIX: the previous version called getDoc() on analytics_daily
 * before writing, but the Firestore rule only allows admin reads on that
 * collection. safeRun() silently swallowed the permission-denied error,
 * so the function always exited before incrementing DAU.
 *
 * NEW PATTERN — zero reads required from the client:
 *   analytics_daily/{day}/dau/{uid}   ← each user writes only their own doc
 *   analytics_daily/{day}/mau/{uid}   ← same for monthly
 *
 * DAU  = count of docs in analytics_daily/{day}/dau/
 * MAU  = count of docs in analytics_daily/{month}/mau/
 *
 * Your admin dashboard should count those subcollection docs instead of
 * reading a dau_count field. This is more accurate and needs no client reads.
 *
 * Required Firestore rule additions (see bottom of this file).
 */

import { db } from "./firebase";
import {
  doc, setDoc, updateDoc, getDoc,
  collection, addDoc, serverTimestamp,
  increment,
} from "firebase/firestore";

// ─── helpers ────────────────────────────────────────────────────────────────
const todayKey = () => new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"
const monthKey = () => new Date().toISOString().slice(0, 7);  // "YYYY-MM"
const safeRun  = async (fn) => {
  try { return await fn(); } catch (err) {
    // Keep this log until you've confirmed DAU is working, then remove it.
    console.error("[analyticsService]", err?.code, err?.message);
  }
};

// ─── trackUserLogin ──────────────────────────────────────────────────────────
/**
 * Call once inside onAuthStateChanged when currentUser becomes truthy.
 *
 * Writes:
 *   analytics_daily/{today}/dau/{uid}         — presence doc (idempotent)
 *   analytics_daily/month_{month}/mau/{uid}   — monthly presence doc
 *   analytics_daily/{today}                   — login_events counter
 *
 * No reads are performed, so no read permission is needed.
 * setDoc with { merge: true } is fully idempotent — safe to call on every login.
 */
export async function trackUserLogin(user) {
  if (!user) return;
  await safeRun(async () => {
    const today = todayKey();
    const month = monthKey();

    const isNewUser =
      user.metadata?.creationTime &&
      Date.now() - new Date(user.metadata.creationTime).getTime() < 86_400_000;

    // 1. Write a presence doc for this user under today's DAU subcollection.
    //    Using the uid as the doc ID makes this naturally idempotent —
    //    logging in twice today won't create a second doc.
    const dauRef = doc(db, "analytics_daily", today, "dau", user.uid);
    await setDoc(dauRef, {
      uid:        user.uid,
      date:       today,
      is_new:     isNewUser ?? false,
      updated_at: serverTimestamp(),
    }, { merge: true });

    // 2. Same pattern for MAU.
    const mauRef = doc(db, "analytics_daily", `month_${month}`, "mau", user.uid);
    await setDoc(mauRef, {
      uid:        user.uid,
      month,
      updated_at: serverTimestamp(),
    }, { merge: true });

    // 3. Increment the raw login_events counter on the day doc (write-only).
    const dayRef = doc(db, "analytics_daily", today);
    await setDoc(dayRef, {
      date:         today,
      login_events: increment(1),
      updated_at:   serverTimestamp(),
    }, { merge: true });
  });
}

// ─── trackChattrSession ──────────────────────────────────────────────────────
export function trackChattrSession(user) {
  if (!user) return () => {};
  let docId = null;
  const startedAt = Date.now();

  safeRun(async () => {
    const today  = todayKey();
    const dayRef = doc(db, "analytics_daily", today);
    await setDoc(dayRef, {
      date:            today,
      chattr_sessions: increment(1),
      chattr_live:     increment(1),
      updated_at:      serverTimestamp(),
    }, { merge: true });

    const ref = await addDoc(collection(db, "analytics_chattr"), {
      uid:        user.uid,
      started_at: serverTimestamp(),
      ended_at:   null,
      duration_s: null,
      date:       today,
    });
    docId = ref.id;
  });

  return () => safeRun(async () => {
    const today  = todayKey();
    const dayRef = doc(db, "analytics_daily", today);
    // Read is still needed here to decrement live count correctly.
    // This is fine — only called on disconnect, not on every login.
    const snap = await getDoc(dayRef);
    if (snap.exists()) {
      const live = Math.max(0, (snap.data().chattr_live || 1) - 1);
      await updateDoc(dayRef, { chattr_live: live, updated_at: serverTimestamp() });
    }
    if (docId) {
      await updateDoc(doc(db, "analytics_chattr", docId), {
        ended_at:   serverTimestamp(),
        duration_s: Math.round((Date.now() - startedAt) / 1000),
      });
    }
  });
}

// ─── trackPageView ───────────────────────────────────────────────────────────
export async function trackPageView(user, page) {
  if (!user || !page) return;
  await safeRun(async () => {
    const today  = todayKey();
    const dayRef = doc(db, "analytics_daily", today);
    const field  = `page_${page.replace(/\//g, "_").replace(/^_/, "")}`;
    await setDoc(dayRef, {
      date:       today,
      [field]:    increment(1),
      updated_at: serverTimestamp(),
    }, { merge: true });
  });
}

// ─── trackEvent ──────────────────────────────────────────────────────────────
export async function trackEvent(type, meta = {}) {
  await safeRun(async () => {
    await addDoc(collection(db, "analytics_events"), {
      type,
      ...meta,
      date:       todayKey(),
      created_at: serverTimestamp(),
    });
  });
}

/*
 * ─── REQUIRED FIRESTORE RULE ADDITIONS ───────────────────────────────────────
 *
 * Replace your current analytics_daily block with this:
 *
 *   match /analytics_daily/{dayDoc} {
 *     allow read: if isAdmin();
 *     allow write: if request.auth != null;
 *
 *     // DAU presence docs — each user writes only their own
 *     match /dau/{uid} {
 *       allow read:  if isAdmin();
 *       allow write: if request.auth != null && request.auth.uid == uid;
 *     }
 *
 *     // MAU presence docs — same
 *     match /mau/{uid} {
 *       allow read:  if isAdmin();
 *       allow write: if request.auth != null && request.auth.uid == uid;
 *     }
 *   }
 *
 * DAU for a given day  = db.collection('analytics_daily/YYYY-MM-DD/dau').count()
 * MAU for a given month = db.collection('analytics_daily/month_YYYY-MM/mau').count()
 */